import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Badge,
  Button,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Skeleton,
  VersionTimeline,
  cn,
  type VersionSourceTone,
} from "@nexus/ui";
import { useTranslation } from "react-i18next";

import { formatCompactLocalDateTime } from "@/features/opportunities/lib/format-relative-time.js";
import type { PersonaScriptDto } from "@/features/persona-opportunities/api/persona-opportunity-scripts.api";
import { getPersonaScriptDisplayTitle } from "@/features/persona-opportunities/lib/script-display-title";
import {
  personaScriptVersionHistoryQueryOptions,
  personaScriptVersionQueryOptions,
  useRestorePersonaScriptVersion,
} from "@/lib/queries/persona-opportunity-scripts.queries";

import { PersonaScriptFieldDiff } from "./PersonaScriptFieldDiff.js";
import {
  highlightedSceneClass,
  personaScriptSceneFieldDiffersFromOriginal,
  sceneFieldBlameByKey,
  sceneFieldBlameKey,
  type PersonaScriptHighlightField,
} from "./persona-script-version-highlight.js";

const SOURCE_I18N: Record<string, { key: string; fallback: string }> = {
  GENERATED: { key: "personaOpportunities.scripts.history.sourceGenerated", fallback: "Generated" },
  MANUAL: { key: "personaOpportunities.scripts.history.sourceManual", fallback: "Manual" },
  AI_EDIT: { key: "personaOpportunities.scripts.history.sourceAiEdit", fallback: "AI edit" },
  RESTORED: { key: "personaOpportunities.scripts.history.sourceRestored", fallback: "Restored" },
};

const SOURCE_TONE: Record<string, VersionSourceTone> = {
  GENERATED: "generated",
  MANUAL: "manual",
  AI_EDIT: "ai",
  RESTORED: "restored",
};

/** Stored write-time labels; "Hook" is the pre-Title name for a title edit. */
const CHANGE_LABEL_I18N: Record<string, { key: string; fallback: string }> = {
  Title: { key: "personaOpportunities.scripts.history.title", fallback: "Title" },
  Hook: { key: "personaOpportunities.scripts.history.title", fallback: "Title" },
};

const SCENE_FIELDS = [
  { field: "seeing" as const, key: "personaOpportunities.scripts.history.seeing", fallback: "Seeing" },
  { field: "hearing" as const, key: "personaOpportunities.scripts.history.hearing", fallback: "Hearing" },
  { field: "product" as const, key: "personaOpportunities.scripts.history.product", fallback: "Product" },
  { field: "props" as const, key: "personaOpportunities.scripts.history.props", fallback: "Props" },
];

const SCENE_GRID_COLUMNS = 2;

export interface ScriptsHistoryDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  setId: string;
  tenantId: string;
  script: PersonaScriptDto;
  canRestore: boolean;
}

export function ScriptsHistoryDrawer({
  open,
  onOpenChange,
  setId,
  tenantId,
  script,
  canRestore,
}: ScriptsHistoryDrawerProps) {
  const { t } = useTranslation();
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null);
  const restore = useRestorePersonaScriptVersion();
  const restoreInFlight = useRef(false);

  const historyQuery = useQuery(
    personaScriptVersionHistoryQueryOptions(tenantId, setId, script.id, { enabled: open }),
  );
  const versions = historyQuery.data?.versions ?? [];
  const latestVersion = historyQuery.data?.activeVersion ?? script.versionCount;

  useEffect(() => {
    if (!open) {
      setSelectedVersion(null);
      return;
    }
    if (selectedVersion == null && latestVersion) {
      setSelectedVersion(latestVersion);
    }
  }, [open, latestVersion, selectedVersion]);

  const previewQuery = useQuery(
    personaScriptVersionQueryOptions(tenantId, setId, script.id, selectedVersion, {
      enabled: open && selectedVersion != null,
    }),
  );
  const originalQuery = useQuery(
    personaScriptVersionQueryOptions(tenantId, setId, script.id, 1, {
      enabled: open,
    }),
  );
  const previousVersion =
    selectedVersion != null && selectedVersion > 1 ? selectedVersion - 1 : null;
  const previousQuery = useQuery(
    personaScriptVersionQueryOptions(tenantId, setId, script.id, previousVersion, {
      enabled: open && previousVersion != null,
    }),
  );

  const preview = previewQuery.isPlaceholderData ? undefined : previewQuery.data?.script;
  const original = originalQuery.data?.script;
  const previous =
    previousVersion != null &&
    !previewQuery.isPlaceholderData &&
    !previousQuery.isPlaceholderData
      ? previousQuery.data?.script
      : undefined;
  const isLatest = selectedVersion != null && selectedVersion === latestVersion;
  const untitled = t("personaOpportunities.scripts.untitledHook", {
    defaultValue: "Script {{slot}}",
    slot: script.slotIndex,
  });
  const title = getPersonaScriptDisplayTitle(preview ?? script, untitled);
  const concept = preview?.concept;
  const viewingVersion = selectedVersion ?? latestVersion;
  const selectedInstruction =
    versions.find((entry) => entry.version === viewingVersion)?.instruction ?? null;
  const versionCount = versions.length || script.versionCount;
  const blameByKey = useMemo(
    () => sceneFieldBlameByKey(versions, viewingVersion),
    [versions, viewingVersion],
  );

  const timelineVersions = useMemo(
    () =>
      versions.map((entry) => {
        const changeCopy = entry.changeLabel
          ? CHANGE_LABEL_I18N[entry.changeLabel]
          : undefined;
        return {
          id: entry.id ?? `v${entry.version}`,
          version: entry.version,
          createdAt: entry.createdAt,
          createdAtLabel: formatCompactLocalDateTime(entry.createdAt, Date.now()),
          createdBy:
            entry.createdBy === "creative_engine"
              ? t("personaOpportunities.scripts.history.creativeEngine", "Creative engine")
              : entry.createdBy,
          sourceBadge: SOURCE_I18N[entry.source]
            ? t(SOURCE_I18N[entry.source].key, SOURCE_I18N[entry.source].fallback)
            : entry.source,
          sourceTone: SOURCE_TONE[entry.source],
          changeLabel: changeCopy
            ? t(changeCopy.key, changeCopy.fallback)
            : entry.changeLabel,
          detail: entry.instruction,
          isCurrentState: entry.version === latestVersion,
        };
      }),
    [latestVersion, t, versions],
  );

  const activeVersionId =
    selectedVersion != null
      ? (versions.find((entry) => entry.version === selectedVersion)?.id ?? `v${selectedVersion}`)
      : undefined;

  function handleRestore() {
    if (selectedVersion == null || isLatest || restore.isPending || restoreInFlight.current) {
      return;
    }
    restoreInFlight.current = true;
    restore.mutate(
      { setId, scriptId: script.id, version: selectedVersion, tenantId },
      {
        onSuccess: (updated) => {
          setSelectedVersion(updated.versionCount);
        },
        onSettled: () => {
          restoreInFlight.current = false;
        },
      },
    );
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full max-w-[1200px] flex-col gap-0 p-0 sm:max-w-[1200px]"
      >
        <SheetHeader className="shrink-0 gap-0 px-6 py-[18px]">
          <div className="flex min-w-0 items-center gap-2 pr-8">
            <SheetTitle className="truncate text-[15px] font-semibold">
              {title}
            </SheetTitle>
            <Badge variant="default" className="rounded-full px-2 py-px text-[11px]">
              {t("personaOpportunities.scripts.history.versionCount", {
                defaultValue_one: "{{count}} version",
                defaultValue_other: "{{count}} versions",
                count: versionCount,
              })}
            </Badge>
          </div>
          <SheetDescription className="mt-[3px] text-xs text-muted-foreground">
            {t("personaOpportunities.scripts.history.subtitle", "Version history")}
          </SheetDescription>
        </SheetHeader>

        <div className="grid min-h-0 flex-1 grid-cols-1 md:grid-cols-[minmax(0,1fr)_280px]">
          <div className="min-h-0 overflow-y-auto border-b border-border px-6 py-5 md:border-b-0 md:border-r">
            <div className="mb-3.5 flex min-h-8 items-start justify-between gap-3.5">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-xs font-semibold">
                    {t("personaOpportunities.scripts.history.viewing", {
                      defaultValue: "Viewing v{{version}}",
                      version: selectedVersion ?? latestVersion,
                    })}
                  </p>
                  {isLatest ? (
                    <span className="rounded-full bg-ai-subtle px-2 py-px text-[10px] font-medium text-ai-subtle-foreground">
                      {t("personaOpportunities.scripts.history.current", "Current")}
                    </span>
                  ) : null}
                </div>
                {concept ? (
                  <p className="mt-[3px] text-xs text-muted-foreground italic">{concept}</p>
                ) : null}
                {preview && previous && previewDiffersFromPrevious(preview, previous) ? (
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    {t(
                      "personaOpportunities.scripts.history.diffLegend",
                      "Struck text was removed. Underlined text is the new wording.",
                    )}
                  </p>
                ) : null}
                {selectedInstruction ? (
                  <div className="mt-2.5">
                    <p className="mb-1 font-mono text-[10px] font-medium tracking-[0.06em] text-muted-foreground uppercase">
                      {t("personaOpportunities.scripts.history.prompt", "Prompt")}
                    </p>
                    <p className="text-xs italic leading-relaxed break-words whitespace-pre-wrap text-ai-text">
                      {t("personaOpportunities.scripts.history.promptQuote", {
                        defaultValue: "“{{prompt}}”",
                        prompt: selectedInstruction,
                      })}
                    </p>
                  </div>
                ) : null}
              </div>
              {canRestore && !isLatest && selectedVersion != null ? (
                <Button
                  size="sm"
                  iconStart="rotate-ccw"
                  onClick={handleRestore}
                  disabled={restore.isPending}
                >
                  {t("personaOpportunities.scripts.history.useThisVersion", "Use this version")}
                </Button>
              ) : null}
            </div>

            {!preview ? (
              <div
                className="flex flex-col gap-3"
                aria-label={t(
                  "personaOpportunities.scripts.history.loadingPreview",
                  "Loading preview…",
                )}
              >
                <Skeleton className="h-36 w-full rounded-sm" />
                <Skeleton className="h-36 w-full rounded-sm" />
              </div>
            ) : (
              <HistoryPreview
                script={preview}
                original={original}
                previous={previous}
                blameByKey={blameByKey}
                onSelectVersion={setSelectedVersion}
              />
            )}
          </div>

          <div className="flex min-h-0 flex-col">
            <div className="shrink-0 px-[18px] pt-4 pb-2">
              <p className="font-mono text-[10px] font-medium tracking-[0.06em] text-muted-foreground uppercase">
                {t("personaOpportunities.scripts.history.versions", "Versions")}
              </p>
              <p className="mt-1 text-[11px] leading-snug text-muted-foreground">
                {t(
                  "personaOpportunities.scripts.history.selectHint",
                  "Select one to preview it",
                )}
              </p>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-[18px] pb-[18px]">
              {historyQuery.isLoading ? (
                <div
                  className="flex flex-col gap-2"
                  aria-label={t(
                    "personaOpportunities.scripts.history.loadingVersions",
                    "Loading versions…",
                  )}
                >
                  <Skeleton className="h-16 w-full" />
                  <Skeleton className="h-16 w-full" />
                  <Skeleton className="h-16 w-full" />
                </div>
              ) : historyQuery.isError ? (
                <div className="flex flex-col items-start gap-2">
                  <p className="text-sm text-muted-foreground">
                    {t(
                      "personaOpportunities.scripts.history.loadError",
                      "Couldn't load version history.",
                    )}
                  </p>
                  <Button size="sm" variant="outline" onClick={() => void historyQuery.refetch()}>
                    {t("personaOpportunities.scripts.history.retry", "Retry")}
                  </Button>
                </div>
              ) : (
                <VersionTimeline
                  variant="rail"
                  versions={timelineVersions}
                  activeVersionId={activeVersionId}
                  onSelect={(entry) => setSelectedVersion(entry.version)}
                  labels={{
                    latest: t("personaOpportunities.scripts.history.latest", "Latest"),
                    empty: t("personaOpportunities.scripts.history.empty", "No versions yet."),
                    select: t("personaOpportunities.scripts.history.select", "Select version"),
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function sceneFieldText(
  script: PersonaScriptDto | undefined,
  sceneNumber: number,
  field: PersonaScriptHighlightField,
): string {
  return script?.scenes.find((scene) => scene.sceneNumber === sceneNumber)?.[field] ?? "";
}

function previewDiffersFromPrevious(
  preview: PersonaScriptDto,
  previous: PersonaScriptDto,
): boolean {
  return preview.scenes.some((scene) =>
    SCENE_FIELDS.some(
      ({ field }) =>
        (scene[field] ?? "") !== sceneFieldText(previous, scene.sceneNumber, field),
    ),
  );
}

function HistoryPreview({
  script,
  original,
  previous,
  blameByKey,
  onSelectVersion,
}: {
  script: PersonaScriptDto;
  original: PersonaScriptDto | undefined;
  previous: PersonaScriptDto | undefined;
  blameByKey: Map<string, number>;
  onSelectVersion: (version: number) => void;
}) {
  return (
    <div className="flex flex-col gap-3">
      {script.scenes.map((scene) => (
        <HistorySceneCard
          key={scene.sceneNumber}
          scene={scene}
          script={script}
          original={original}
          previous={previous}
          blameByKey={blameByKey}
          onSelectVersion={onSelectVersion}
        />
      ))}
    </div>
  );
}

function HistorySceneCard({
  scene,
  script,
  original,
  previous,
  blameByKey,
  onSelectVersion,
}: {
  scene: PersonaScriptDto["scenes"][number];
  script: PersonaScriptDto;
  original: PersonaScriptDto | undefined;
  previous: PersonaScriptDto | undefined;
  blameByKey: Map<string, number>;
  onSelectVersion: (version: number) => void;
}) {
  const { t } = useTranslation();
  const empty = t("personaOpportunities.scripts.history.emDash", "—");

  return (
    <article className="overflow-hidden rounded-sm border border-border">
      <header className="flex items-center gap-2 border-b border-border bg-muted px-3 py-[9px]">
        <span className="text-[11px] tabular-nums tracking-[-0.01em] text-muted-foreground">
          {t("personaOpportunities.scripts.history.sceneNumber", {
            defaultValue: "{{number}}",
            number: scene.sceneNumber,
          })}
        </span>
        <span className="rounded-sm border border-border bg-background px-2 py-px text-[11px] text-foreground">
          {scene.sceneName}
        </span>
      </header>
      <dl className="grid grid-cols-2">
        {SCENE_FIELDS.map(({ field, key, fallback }, index) => {
          const isDifferent = personaScriptSceneFieldDiffersFromOriginal(
            script,
            original,
            scene.sceneNumber,
            field,
          );
          const blameVersion = isDifferent
            ? (blameByKey.get(sceneFieldBlameKey(scene.sceneNumber, field)) ?? null)
            : null;

          return (
            <div
              key={field}
              className={cn(
                "px-3 py-[9px]",
                index % SCENE_GRID_COLUMNS === 0 && "border-r border-border",
                index < SCENE_GRID_COLUMNS && "border-b border-border",
                highlightedSceneClass(isDifferent),
              )}
            >
              <dt className="mb-1 flex items-center justify-between gap-2">
                <span className="font-mono text-[10px] font-medium tracking-[0.06em] text-muted-foreground uppercase">
                  {t(key, fallback)}
                </span>
                {blameVersion != null ? (
                  <button
                    type="button"
                    onClick={() => onSelectVersion(blameVersion)}
                    className="shrink-0 cursor-pointer font-mono text-[10px] font-medium tabular-nums tracking-normal text-ai-text transition-colors duration-150 ease-out hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
                    aria-label={t("personaOpportunities.scripts.history.changedInVersion", {
                      defaultValue: "Changed in v{{version}}",
                      version: blameVersion,
                    })}
                  >
                    {t("personaOpportunities.scripts.history.versionShort", {
                      defaultValue: "v{{version}}",
                      version: blameVersion,
                    })}
                  </button>
                ) : null}
              </dt>
              <dd className="text-xs leading-[1.55] whitespace-pre-wrap text-foreground">
                <PersonaScriptFieldDiff
                  before={
                    previous
                      ? sceneFieldText(previous, scene.sceneNumber, field)
                      : undefined
                  }
                  after={scene[field] ?? ""}
                  empty={empty}
                />
              </dd>
            </div>
          );
        })}
      </dl>
    </article>
  );
}
